import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Home, Sparkles, X, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import MiniRouletteLogo from './MiniRouletteLogo';

const WHEEL_R = 180;
const FALLBACK_COLORS = ['#E11D48', '#F59E0B', '#004D61', '#3B82F6', '#006B85'];

// Build a pie-slice path between two angles (degrees, 0 = top, clockwise).
function buildSegPath(startDeg, endDeg, r) {
  const sweep = endDeg - startDeg;
  const sRad = (startDeg - 90) * (Math.PI / 180);
  const eRad = (endDeg - 90) * (Math.PI / 180);
  const x1 = r * Math.cos(sRad);
  const y1 = r * Math.sin(sRad);
  const x2 = r * Math.cos(eRad);
  const y2 = r * Math.sin(eRad);
  const largeArc = sweep > 180 ? 1 : 0;
  return `M 0 0 L ${x1.toFixed(3)} ${y1.toFixed(3)} A ${r} ${r} 0 ${largeArc} 1 ${x2.toFixed(3)} ${y2.toFixed(3)} Z`;
}

// Split label "X% Deposit Bonus" / "$5 Freeplay" / "3 Free Spins" into 2 lines for the wedge.
function splitLabel(label) {
  if (!label) return ['', ''];
  const tokens = label.split(' ');
  if (tokens.length === 1) return [label, ''];
  // Take first 1-2 tokens as line1, the rest as line2.
  if (tokens.length === 2) return [tokens[0], tokens[1]];
  return [tokens.slice(0, 2).join(' '), tokens.slice(2).join(' ')];
}

// Pick a weighted random index based on segments[].chance.
function weightedPick(segments) {
  const total = segments.reduce((s, x) => s + Math.max(0, x.chance || 0), 0);
  if (total <= 0) return Math.floor(Math.random() * segments.length);
  const roll = Math.random() * total;
  let acc = 0;
  for (let i = 0; i < segments.length; i++) {
    acc += Math.max(0, segments[i].chance || 0);
    if (roll <= acc) return i;
  }
  return segments.length - 1;
}

export default function RoulettePage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const homePath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const explorePath = tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore';

  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const wheelRef = useRef(null);
  const lockYRef = useRef(0);

  useEffect(() => {
    let alive = true;
    api.get('/api/roulette/segments')
      .then(res => {
        if (!alive) return;
        const items = (res.data?.segments || []).filter(s => s.is_active);
        setSegments(items);
      })
      .catch(() => toast.error('Failed to load roulette'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  // Lock body scroll while the wheel is spinning (mobile fix).
  useEffect(() => {
    if (!spinning) return;
    const y = window.scrollY;
    lockYRef.current = y;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${y}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, lockYRef.current);
    };
  }, [spinning]);

  // Visually equal-size wedges (one slice per segment).
  // Winning probability still works on `chance` via weightedPick().
  const wedges = useMemo(() => {
    if (segments.length === 0) return [];
    const sweep = 360 / segments.length;
    return segments.map((s, i) => {
      const start = i * sweep;
      const end = start + sweep;
      const center = start + sweep / 2;
      return {
        i,
        label: s.label,
        chance: s.chance,
        color: s.color || FALLBACK_COLORS[i % FALLBACK_COLORS.length],
        path: buildSegPath(start, Math.min(end, 359.999), WHEEL_R),
        textAngle: center,
        sweep,
      };
    });
  }, [segments]);

  const spin = async () => {
    if (spinning || wedges.length === 0) return;
    setResult(null);
    setSpinning(true);
    try {
      const res = await api.post('/api/roulette/spin');
      const data = res.data || {};
      const targetIdx = Math.max(0, Math.min(segments.length - 1, Number(data.segment_index ?? 0)));
      const segCenterDeg = wedges[targetIdx].textAngle;
      const desiredMod = (360 - segCenterDeg + 360) % 360;
      const currentMod = ((rotation % 360) + 360) % 360;
      const turns = 5 + Math.floor(Math.random() * 3);
      const delta = ((desiredMod - currentMod + 360) % 360) + turns * 360;
      setRotation(rotation + delta);
      const chosenLabel = data.segment?.label || segments[targetIdx]?.label || '';
      const chosenChance = data.segment?.chance ?? segments[targetIdx]?.chance ?? 0;
      const rewardFp = Number(data.reward_fp || 0);
      setTimeout(() => {
        setSpinning(false);
        setResult({ idx: targetIdx, label: chosenLabel, chance: chosenChance, rewardFp });
        if (rewardFp > 0) toast.success(`+${rewardFp} FP credited`);
      }, 5100);
    } catch (e) {
      setSpinning(false);
      const msg = e.response?.data?.detail || e.response?.data?.message || 'Spin failed';
      toast.error(msg);
    }
  };

  return (
    <div
      className="player-app min-h-screen pb-12"
      style={{ color: '#FFFFFF' }}
      data-testid="explore-sub-roulette"
    >
      {/* Header */}
      <div
        className="sticky top-0 z-30 px-4 sm:px-6 py-4 flex items-center gap-3"
        style={{
          background: 'rgba(6,23,14,0.85)',
          backdropFilter: 'blur(18px) saturate(160%)',
          WebkitBackdropFilter: 'blur(18px) saturate(160%)',
          borderBottom: '1px solid rgba(239,68,68,0.45)',
        }}
      >
        <button
          onClick={() => navigate(explorePath)}
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#FFFFFF',
          }}
          data-testid="roulette-back"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1
            className="display font-extrabold text-xl sm:text-2xl flex items-center gap-2"
            style={{ color: '#FFFFFF' }}
          >
            <MiniRouletteLogo size={36} spin />
            Roulette
          </h1>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Spin daily — try your luck!
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-6 sm:px-6 pt-10 pb-8 flex flex-col items-center">
        {loading ? (
          <div className="py-16 text-center text-white/70">Loading wheel...</div>
        ) : wedges.length === 0 ? (
          <div className="py-16 text-center text-white/70 max-w-md">
            <Sparkles className="w-10 h-10 mx-auto mb-3 text-amber-400" />
            <p className="display font-bold text-lg">No prizes configured yet</p>
            <p className="text-sm mt-1">An admin must add roulette segments before the wheel can spin.</p>
          </div>
        ) : (
          <>
            {/* Wheel container */}
            <div
              className="relative mb-6"
              style={{
                width: 'min(82vw, 380px)',
                aspectRatio: '1 / 1',
                touchAction: spinning ? 'none' : 'auto',
              }}
              data-testid="roulette-wheel-container"
            >
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background:
                    'radial-gradient(circle, rgba(239,68,68,0.18) 60%, rgba(239,68,68,0) 75%)',
                  filter: 'blur(8px)',
                }}
                aria-hidden
              />

              {/* Rotating wheel */}
              <div
                ref={wheelRef}
                className="absolute inset-0 will-change-transform"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  transition: spinning
                    ? 'transform 5s cubic-bezier(0.17, 0.67, 0.16, 1)'
                    : 'transform 0s',
                  transformOrigin: '50% 50%',
                  filter:
                    'drop-shadow(0 18px 30px rgba(0,0,0,0.55)) drop-shadow(0 0 14px rgba(239,68,68,0.35))',
                }}
                data-testid="roulette-wheel"
              >
                <svg viewBox="-200 -200 400 400" className="w-full h-full block">
                  {/* Outer gold ring */}
                  <circle r={WHEEL_R + 8} fill="#1F1207" stroke="#FFD93D" strokeWidth="6" />
                  {/* Studded dots — match wedge count */}
                  {wedges.map((w, k) => {
                    const a = (w.textAngle - w.sweep / 2) * (Math.PI / 180);
                    return (
                      <circle
                        key={k}
                        cx={(WHEEL_R + 8) * Math.cos(a - Math.PI / 2)}
                        cy={(WHEEL_R + 8) * Math.sin(a - Math.PI / 2)}
                        r="3.5"
                        fill="#FFF8C7"
                      />
                    );
                  })}

                  {/* Segments */}
                  {wedges.map((w) => {
                    const [line1, line2] = splitLabel(w.label);
                    // Auto font size based on sweep (smaller wedge = smaller text).
                    // For 12+ wedges (sweep < 30) we shrink hard so 2-line labels
                    // like "5% Deposit Bonus" fit inside the wedge without
                    // bleeding into neighbours.
                    const fontSize = w.sweep < 16 ? 8 : w.sweep < 22 ? 9 : w.sweep < 32 ? 10 : 13;
                    return (
                      <g key={w.i}>
                        <path
                          d={w.path}
                          fill={w.color}
                          stroke="#FFFFFF"
                          strokeWidth="1.6"
                          strokeLinejoin="round"
                        />
                        <text
                          textAnchor="middle"
                          fontFamily="'Plus Jakarta Sans', sans-serif"
                          fontWeight="800"
                          fontSize={fontSize}
                          fill="#FFFFFF"
                          style={{ paintOrder: 'stroke' }}
                          stroke="rgba(0,0,0,0.55)"
                          strokeWidth="1"
                          transform={`rotate(${w.textAngle})`}
                        >
                          <tspan x="0" y={-(WHEEL_R * 0.72)}>{line1}</tspan>
                          {line2 && <tspan x="0" dy={fontSize + 1}>{line2}</tspan>}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              </div>

              {/* Static pointer (top, pointing down) */}
              <div
                className="absolute left-1/2 -translate-x-1/2 z-10"
                style={{ top: '-6px' }}
                aria-hidden
              >
                <svg width="44" height="48" viewBox="0 0 44 48">
                  <defs>
                    <linearGradient id="ptr-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#FFD93D" />
                      <stop offset="1" stopColor="#F59E0B" />
                    </linearGradient>
                  </defs>
                  <path d="M22 46 L4 6 A 18 18 0 0 1 40 6 Z" fill="url(#ptr-grad)" stroke="#FFFFFF" strokeWidth="3" />
                </svg>
              </div>

              {/* Static center hub */}
              <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center z-10"
                style={{
                  width: '76px',
                  height: '76px',
                  background: '#0B0F0C',
                  border: '4px solid #FFD93D',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.6), inset 0 0 8px rgba(255,217,61,0.3)',
                }}
                aria-hidden
              >
                <span
                  className="display font-extrabold tracking-widest"
                  style={{ color: '#FFD93D', fontSize: '12px' }}
                >
                  SPIN
                </span>
              </div>
            </div>

            {/* SPIN button */}
            <button
              onClick={spin}
              disabled={spinning}
              className="display font-extrabold rounded-2xl text-white px-10 py-4 text-base sm:text-lg transition active:scale-[0.98] hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(180deg, var(--vl-page-roulette-accent, #EF4444), #B91C1C)',
                border: '2px solid #FFFFFF',
                boxShadow:
                  '0 0 0 1px rgba(255,255,255,0.5), 0 10px 26px rgba(239,68,68,0.5), inset 0 1px 0 rgba(255,255,255,0.25)',
                letterSpacing: '0.06em',
              }}
              data-testid="roulette-spin-btn"
            >
              {spinning ? 'Spinning...' : 'SPIN THE WHEEL'}
            </button>

            {/* Prize legend */}
            <details className="mt-8 w-full max-w-2xl">
              <summary
                className="cursor-pointer text-sm display font-bold flex items-center gap-2 select-none px-4 py-2 rounded-xl"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#FFD93D',
                }}
              >
                <Sparkles className="w-4 h-4" /> View all {segments.length} prizes &amp; chances
              </summary>
              <ul
                className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs sm:text-sm"
                data-testid="roulette-prize-list"
              >
                {segments.map((p, i) => (
                  <li
                    key={p.id || i}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      color: 'rgba(255,255,255,0.85)',
                    }}
                  >
                    <span
                      className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold"
                      style={{ background: p.color, color: '#fff' }}
                    >
                      {i + 1}
                    </span>
                    <span className="flex-1">{p.label}</span>
                    <span className="display font-extrabold text-amber-300">{Number(p.chance).toFixed(0)}%</span>
                  </li>
                ))}
              </ul>
            </details>
          </>
        )}
      </div>

      {/* Win modal */}
      {result && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center px-4"
          style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(10px)' }}
          onClick={() => setResult(null)}
          data-testid="roulette-result-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-3xl w-full max-w-md p-6 sm:p-8 text-center relative"
            style={{
              background: 'linear-gradient(180deg, #FFFFFF, #FFF7ED)',
              color: '#0B0F0C',
              boxShadow: '0 30px 70px rgba(0,0,0,0.5)',
            }}
          >
            <button
              onClick={() => setResult(null)}
              className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition active:scale-95"
              style={{
                background: 'rgba(0,0,0,0.06)',
                border: '1px solid rgba(0,0,0,0.08)',
                color: '#0B0F0C',
              }}
              aria-label="Close"
              data-testid="roulette-result-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div
              className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-4"
              style={{
                background: 'linear-gradient(180deg, #FFD93D, #F59E0B)',
                boxShadow: '0 12px 30px rgba(245,158,11,0.55), inset 0 1px 0 rgba(255,255,255,0.5)',
              }}
            >
              <Trophy className="w-10 h-10" style={{ color: '#3F2A00' }} strokeWidth={2.5} />
            </div>
            <p
              className="display text-xs uppercase tracking-[0.2em] font-bold"
              style={{ color: '#92400E' }}
            >
              You won
            </p>
            <h2
              className="display font-extrabold text-2xl sm:text-3xl mt-1 mb-2"
              style={{ color: '#0B0F0C' }}
            >
              {result.label}
            </h2>
            <p className="text-sm" style={{ color: '#6B7280' }}>
              Congrats! Your reward will be credited shortly.
            </p>
            <button
              onClick={() => setResult(null)}
              className="mt-6 w-full h-12 rounded-2xl display font-extrabold text-white transition active:scale-[0.98] hover:brightness-110"
              style={{
                background: 'linear-gradient(180deg, var(--vl-page-roulette-accent, #EF4444), #B91C1C)',
                boxShadow: '0 8px 22px rgba(239,68,68,0.45)',
              }}
              data-testid="roulette-result-ok"
            >
              Awesome!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
